import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSlotT } from "@/cms/useSlotT";
import { pickLocalized } from "@/cms/pickLocale";
import { cmsDateSortValue } from "@/cms/formatDate";
import { Icon } from "@iconify/react";
import { Container, Reveal, PageHeader, Tabs, type TabItem } from "@/components/ui";
import { H2 } from "@/components/ui/typography";
import { LECTURES, FEATURED_LECTURE, type Lecture } from "@/content/lectures";
// `lectures.generated.ts` is regenerated by `npm run cms:sync` and committed
// to git, so a static import is safe (the dev server's concurrently script
// also re-runs cms:sync on every boot). The previous `require()` + try/catch
// pattern never resolved at browser runtime — Vite serves it as raw JS, where
// `require` is undefined, so the catch always reset `cmsLectures` to [] and
// the page silently fell back to the static `LECTURES` array.
import { cmsLectures } from "@/content/cms/lectures.generated";
import { useCmsLectures } from "@/hooks/useCmsLectures";
import { useCmsLectureTopics } from "@/hooks/useCmsLectureTopics";
import { useCmsArticles, selectColumnArticles, sortArticlesNewestFirst } from "@/hooks/useCmsArticles";
import { cmsMediaLargestWebpUrl } from "@/utils/cmsMediaUrl";
import CmsImage from "@/components/media/CmsImage";
import LectureCard, { type NormalizedLecture } from "@/components/cards/LectureCard";
import SEO from "@/utils/seo";
import { buildBreadcrumbList } from "@/utils/structuredData";
import { SITE } from "@/content/site";
import { buildLocalePath, type Locale } from "@/utils/localePath";

/**
 * The `sizes` for a card image in the 2-up grid (`md:grid-cols-2`).
 *
 * The grid sits in the 1200px container, so a column is ≈588px there — and
 * '50vw' is 600px at the same viewport, which overstates it by a hair. On a
 * wider screen 50vw overstates it further. Overstating is the safe
 * direction: a `sizes` that is too small makes the browser upscale.
 *
 * 2026-11-03: this is now used ONLY by the 「文章分享」 tab's article grid.
 * The 「預約講座主題」 tab no longer renders images at all — its topics are
 * a plain list (see the panel below).
 */
const TWO_UP_IMAGE_SIZES = "(min-width: 768px) 50vw, 100vw";

/**
 * LecturesPage — archive of school talks and public lectures.
 *
 * Renders every entry as a card, newest first. Each card links to its
 * dedicated detail route (/lectures/<slug>).
 *
 * Data source: `useCmsLectures()` — see src/hooks/useCmsLectures.ts. It seeds
 * from the build-time `cmsLectures` snapshot (so the prerendered HTML is
 * complete for crawlers and the page still renders if the CMS is down) and
 * replaces it with a live `/api/cms/public/lectures` read on mount. The
 * static `LECTURES` fallback in `@/content/lectures` is intentionally empty
 * as of 2026-10-12 — every lecture is in the CMS now. The page only pulls
 * metadata (slug, date, host, venue, audience, photo, title, summary); full
 * body sections live in the CMS and are rendered by `LectureDetailPage`.
 */

/**
 * Map a single CMS index entry into the static `Lecture` shape so the
 * existing `LectureCard` can consume it uniformly.
 *
 * 2026-10-13: title/summary now have per-locale fields (`title_zhHant`,
 * `title_zhHans`, `title_en`) baked into the generated TS. The collapsed
 * `title` / `summary` from the wire (locale-aware) remains as a fallback
 * for any field the new per-locale column happens to be empty for.
 * Dimensions default to 0, which simply omits the `width`/`height` attributes
 * rather than reserving a wrong aspect ratio.
 */
function cmsToLecture(cms: any): Lecture {
  const titleZh = cms.title_zhHant ?? cms.title ?? "";
  const titleHans = cms.title_zhHans ?? cms.title ?? "";
  const titleEn = cms.title_en ?? cms.title ?? "";
  const summaryZh = cms.summary_zhHant ?? cms.summary ?? "";
  const summaryHans = cms.summary_zhHans ?? cms.summary ?? "";
  const summaryEn = cms.summary_en ?? cms.summary ?? "";
  const audience =
    cms.audience === "teacher" ||
    cms.audience === "parent" ||
    cms.audience === "student"
      ? cms.audience
      : "teacher";
  return {
    slug: cms.slug ?? "",
    date: cms.event_date ?? "",
    audience,
    speaker: cms.speaker ?? "",
    hostHans: cms.host_zhHans ?? "",
    hostZh: cms.host_zhHant ?? "",
    hostEn: cms.host_en ?? "",
    venueHans: cms.venue_zhHans ?? "",
    venueZh: cms.venue_zhHant ?? "",
    venueEn: cms.venue_en ?? "",
    // 2026-10-20: the host organisation's official website, so the venue
    // name on the card links out to it.
    venueUrl: cms.venue_url ?? undefined,
    seriesHans: cms.series_zhHans ?? "",
    seriesZh: cms.series_zhHant ?? "",
    seriesEn: cms.series_en ?? "",
    externalUrl: cms.external_url ?? undefined,

    // 2026-10-13: per-locale title/summary. The server pre-resolves empty
    // `_zhHans` columns via OpenCC, so a Simplified reader sees a Simplified
    // card even when the operator hasn't backfilled the column yet.
    titleHans: titleHans,
    titleZh: titleZh,
    titleEn: titleEn,
    summaryHans: summaryHans,
    summaryZh: summaryZh,
    summaryEn: summaryEn,

    photo1: cms.photo1_url ?? "",
    // The CMS stores one URL per photo (no separate thumb column), so the
    // card falls back to the full-size asset — same as LectureDetailPage.
    photo1Thumb: cms.photo1_url ?? "",
    photo1Width: cms.photo1_width ?? 0,
    photo1Height: cms.photo1_height ?? 0,
    photo1AltZh: cms.photo1_alt_zhHant ?? "",
    // 2026-10-25: the Simplified alt was in the DB all along, but the card
    // only ever had the Traditional and English columns, so a 简体中文
    // reader was served the Traditional alt.
    photo1AltHans: cms.photo1_alt_zhHans ?? "",
    photo1AltEn: cms.photo1_alt_en ?? "",

    photo2: cms.photo2_url ?? "",
    photo2Thumb: cms.photo2_url ?? "",
    photo2Width: cms.photo2_width ?? 0,
    photo2Height: cms.photo2_height ?? 0,
    photo2AltZh: cms.photo2_alt_zhHant ?? "",
    photo2AltHans: cms.photo2_alt_zhHans ?? "",
    photo2AltEn: cms.photo2_alt_en ?? "",

    // 2026-10-18: SEO overrides. The server list endpoint now exposes
    // meta_title_zhHant/_zhHans/_en + meta_description_zhHant/_zhHans/_en
    // alongside the visible fields; cms:sync stashes them in
    // lectures.generated.ts. Without these, LectureDetailPage's
    // metaTitleFor always sees undefined and falls back to the long visible
    // title — making the Chinese meta backfill in the DB invisible at
    // prerender time.
    metaTitleZh: cms.meta_title_zhHant ?? "",
    metaTitleHans: cms.meta_title_zhHans ?? "",
    metaTitleEn: cms.meta_title_en ?? "",
    metaDescriptionZh: cms.meta_description_zhHant ?? "",
    metaDescriptionHans: cms.meta_description_zhHans ?? "",
    metaDescriptionEn: cms.meta_description_en ?? "",
  };
}

/**
 * Sort newest-first, with unparseable dates sinking to the end.
 *
 * 2026-10-16: this compared `a.date < b.date` as strings, on the comment's
 * assumption that `event_date` is always ISO 8601 and therefore sorts
 * lexically. Nothing enforced that — a row ingested the same day carried
 * `"2026年10月13日"`, and '2' (0x32) sorts above '-' (0x2D), so that lecture
 * jumped to the top of the archive for a reason unrelated to its date. Parse
 * to a timestamp and compare numerically; `MagazinePage.tsx` already does
 * this and carries the same note.
 */
function sortLecturesNewestFirst(list: Lecture[]): Lecture[] {
  return list.sort((a, b) => cmsDateSortValue(b.date) - cmsDateSortValue(a.date));
}

/**
 * Headline lecture for the homepage news-teaser — prefers CMS-authored
 * entries when available, falls back to the static FEATURED_LECTURE so the
 * teaser still has something to link to during CMS ramp-up.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const displayedFeatured: Lecture | undefined =
  cmsLectures.length > 0
    ? sortLecturesNewestFirst(cmsLectures.map(cmsToLecture))[0]
    : FEATURED_LECTURE;

/**
 * How many column articles the 「總監專欄」 tab shows before the reader has
 * to ask for more.
 *
 * Three is what fits above the fold on a laptop in a two-column grid — two
 * full rows of cards — so the tab reads as "a column with substance" rather
 * than "an empty list with a promise". Each click of 載入更多 adds another
 * three, and the button disappears once everything is on screen.
 */
const COLUMN_PAGE_SIZE = 3;

/**
 * The stagger delay for a card's reveal animation, in seconds.
 *
 * 2026-10-21: the delay was `idx * 0.05` with no ceiling. With the
 * load-more button in place that becomes a real defect — the twelfth card
 * would wait 0.6 s to animate, the thirtieth 1.5 s — and the wait grows
 * every time the reader clicks, because `idx` keeps climbing. Capping the
 * multiplier at 6 keeps the "cards cascade in" effect for the first screen
 * while making every later card feel instant.
 */
const REVEAL_STAGGER_CAP = 6;

export default function LecturesPage() {
  const slotT = useSlotT("lectures");
  const { t, i18n } = useTranslation();

  // The active locale, for the one internal link this page renders (the
  // contact form in the 「查詢其他客製主題」 block). Every public route
  // carries a locale prefix, so a bare `/contact` would be redirected by
  // the app's own BARE_REDIRECTS table — building the path directly avoids
  // the extra hop and keeps the link correct in the prerendered HTML.
  const locale = (i18n.language || "zh-Hant") as Locale;

  // 2026-10-16: was a module-level `const displayedLectures` built once from
  // the bundled `cmsLectures` snapshot, so a lecture published after the last
  // build never appeared here even though the API and the detail page both
  // had it. The hook seeds from the same snapshot (keeping the prerendered
  // HTML and the offline case intact) and swaps in the live list on mount.
  const liveLectures = useCmsLectures();
  const displayedLectures = useMemo(
    () =>
      liveLectures.length > 0
        ? sortLecturesNewestFirst(liveLectures.map(cmsToLecture))
        : LECTURES,
    [liveLectures],
  );

  // Topics come from the CMS (`cms_lecture_topics`), not from the i18n JSON.
  // The hook seeds from the build-time snapshot so the prerendered HTML is
  // complete, then swaps in the live list on mount.
  //
  // The old implementation read `lectures.topics.items` from the i18n JSON
  // with `returnObjects: true`. That shape cannot be edited in the CMS — a
  // SlotDef is a single scalar string — so the four topics were hard-coded
  // copy that only a developer could change.
  const topics = useCmsLectureTopics();

  // The Executive Director's column. Selected by the `總監專欄` tag rather
  // than by a hard-coded slug, so publishing a new column article needs no
  // code change.
  const allArticles = useCmsArticles();
  const columnArticles = useMemo(
    () => sortArticlesNewestFirst(selectColumnArticles(allArticles)),
    [allArticles],
  );

  /**
   * How many column articles are on screen.
   *
   * Reset whenever the underlying list changes — otherwise the reader who
   * expanded the list and then had the CMS publish a new article would be
   * looking at a stale cut-off count.
   *
   * The dependency is the article SLUGS, not the array. `columnArticles` is
   * rebuilt by `useMemo` whenever `allArticles` changes identity, and
   * `useCmsArticles` returns a fresh array on every render — so keying on
   * the array itself would reset the count on every single render and the
   * 載入更多 button would never do anything. The slug list changes only when
   * the set of articles actually changes.
   */
  const [columnVisible, setColumnVisible] = useState(COLUMN_PAGE_SIZE);
  const columnSlugs = useMemo(
    () => columnArticles.map((a) => a.slug).join("|"),
    [columnArticles],
  );
  useEffect(() => {
    setColumnVisible(COLUMN_PAGE_SIZE);
  }, [columnSlugs]);

  const columnShown = columnArticles.slice(0, columnVisible);
  const columnRemaining = columnArticles.length - columnShown.length;

  const tabs: TabItem[] = [
    {
      id: "topics",
      label: t("lectures.tabs.topics"),
      count: topics.length || undefined,
    },
    {
      id: "column",
      // 2026-10-22: the visible name was changed from 「總監專欄」 to
      // 「文章分享」 at the Executive Director's request — the audience
      // responds better to a plain "articles" label than to a title that
      // names a role.
      //
      // ONLY THE LABEL CHANGED. The content is still the articles tagged
      // 「總監專欄」 in the CMS (see `selectColumnArticles`), and the i18n
      // key stays `column` for that reason — the key describes WHAT the tab
      // holds, the label describes how it is presented. Do not "fix" the
      // filter to match the new label: the two are deliberately different,
      // and the same split applies to `columnHint` / `columnEmpty` /
      // `author`, which still name the Executive Director.
      label: t("lectures.tabs.column"),
      count: columnArticles.length || undefined,
    },
  ];

  return (
    <>
      <SEO
        title={slotT("seo.lecturesTitle")}
        description={slotT("seo.lecturesDescription")}
        keywords={slotT("seo.lecturesKeywords")}
        jsonLd={buildBreadcrumbList([
          { name: t("nav.home"), path: "/" },
          { name: t("nav.lectures"), path: "/lectures" },
        ])}
      />
      {/* No `tag`: the eyebrow line repeated the menu label (「講座分享」).
          2026-11-04: the hero title is painted again — the page needs a
          visible <h1> that says what the page is, and the 「預約講座主題」
          section below no longer carries that job on its own. */}
      <PageHeader
        title={slotT("lectures.hero.title")}
        description={slotT("lectures.hero.description")}
      />

      {/* Bookable topics + the Executive Director's column.
          2026-10-21: the section became a tabbed panel. The CEO asked to
          publish thought-leadership articles so school clients can see his
          educational philosophy; putting them behind a tab beside the
          bookable topics keeps the booking path (the page's primary job)
          one click away, while giving the essays a permanent home on a page
          schools already visit.

          The tab STRIP is UI state, not content — the labels are CMS-editable
          via slot overrides, but which tab is open is not persisted. */}
      <section
        className="py-section-y px-section-x bg-surface"
        aria-labelledby="lectures-topics-title"
      >
        <Container size="xl">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-8">
              <span className="section-title__tagline inline-block mb-3">
                {t("lectures.topics.tag")}
              </span>
              <H2
                id="lectures-topics-title"
                className="mb-3"
              >
                {t("lectures.topics.title")}
              </H2>
            </div>
          </Reveal>

          <Tabs items={tabs} label={t("lectures.tabs.label")}>
            {(activeId) =>
              activeId === "topics" ? (
                /* ── Tab 1: bookable topics ──
                   2026-11-03: the four topics were rendered as a 2-up grid of
                   image cards. The design note asked for the opposite: a plain
                   LIST of the possible talk topics — no card box, no feature
                   image, no shadow. The card chrome made the section read as
                   "four showcases" rather than "a catalogue of what you can
                   book", and the illustrations added nothing a reader could
                   act on.

                   So the topics are now a numbered list on one column. The
                   number and audience badge survive (they carry the CMS
                   `number` and `audience` fields); the description stays
                   because it IS the topic detail. The image is simply not
                   rendered — the media remains in the CMS and the admin
                   screen, so an operator can bring it back without
                   re-uploading.

                   The tab's `topicsHint` line is replaced by a proper welcome
                   block (title + paragraph), and a "query other custom
                   topics" block is appended so a visitor whose need is not
                   in the list has somewhere to go. */
                <div className="max-w-3xl mx-auto">
                  {/* Welcome */}
                  <div className="text-center mb-10">
                    <h3 className="text-xl md:text-2xl font-bold text-ink mb-3">
                      {t("lectures.tabs.welcomeTitle")}
                    </h3>
                    <p className="text-base text-ink-soft leading-relaxed">
                      {t("lectures.tabs.welcomeText")}
                    </p>
                  </div>

                  {topics.length === 0 ? (
                    <p className="text-center text-ink-muted">
                      {t("lectures.tabs.topicsEmpty")}
                    </p>
                  ) : (
                    <ol className="grid sm:grid-cols-2 gap-x-10 border-t border-b border-border">
                      {topics.map((item, idx) => (
                        <Reveal
                          key={item.id}
                          as="li"
                          delay={Math.min(idx, REVEAL_STAGGER_CAP) * 0.05}
                          className="border-b border-border py-6 md:py-7 last:border-b-0 [&:nth-last-child(2):nth-child(odd)]:border-b-0"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-primary font-bold text-2xl leading-none tabular-nums">
                              {item.number}
                            </span>
                            {item.audience && (
                              <span className="inline-flex bg-primary/10 text-primary px-3 py-1 rounded-yada-full text-xs font-semibold">
                                {item.audience}
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-ink mb-2 leading-snug">
                            {item.title}
                          </h3>
                          <p className="text-base text-ink-soft leading-relaxed">
                            {item.description}
                          </p>
                        </Reveal>
                      ))}
                    </ol>
                  )}

                  {/* Enquire about other custom topics */}
                  <div className="mt-12 bg-surface-2 rounded-yada-xl border border-border p-6 md:p-8 text-center">
                    <h3 className="text-lg md:text-xl font-bold text-ink mb-3">
                      {t("lectures.tabs.customTitle")}
                    </h3>
                    <p className="text-base text-ink-soft leading-relaxed max-w-2xl mx-auto mb-6">
                      {t("lectures.tabs.customText")}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <a
                        href={SITE.contact.whatsapp.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-primary text-surface px-7 py-3 rounded-yada-full font-semibold hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
                      >
                        <Icon icon="lucide:message-circle" />
                        <span>{t("lectures.tabs.customWhatsapp")}</span>
                      </a>
                      <a
                        href={buildLocalePath("/contact", locale)}
                        className="inline-flex items-center gap-2 bg-transparent text-primary border-2 border-primary px-7 py-3 rounded-yada-full font-semibold hover:bg-primary hover:text-surface transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
                      >
                        <span>{t("lectures.tabs.customForm")}</span>
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── Tab 2: the Executive Director's column ── */
                <div>
                  <p className="text-center text-base text-ink-soft mb-8 max-w-3xl mx-auto">
                    {t("lectures.tabs.columnHint")}
                  </p>
                  {columnArticles.length === 0 ? (
                    <p className="text-center text-ink-muted">
                      {t("lectures.tabs.columnEmpty")}
                    </p>
                  ) : (
                    <>
                      <div className="grid gap-6 md:grid-cols-2">
                        {columnShown.map((article, idx) => {
                          const imageUrl = cmsMediaLargestWebpUrl(article.coverMediaUuid);
                          return (
                            <Reveal
                              key={article.slug}
                              delay={Math.min(idx, REVEAL_STAGGER_CAP) * 0.05}
                              className="h-full"
                            >
                              <article className="bg-white rounded-yada-xl border border-border shadow-yada overflow-hidden h-full flex flex-col">
                                {imageUrl && (
                                  <CmsImage
                                    uuid={article.coverMediaUuid}
                                    alt={article.title}
                                    sizes={TWO_UP_IMAGE_SIZES}
                                    className="w-full aspect-[16/10] object-cover"
                                  />
                                )}
                                <div className="p-6 flex flex-col flex-1">
                                  <p className="text-xs font-semibold text-primary mb-2">
                                    {t("lectures.tabs.author")}
                                  </p>
                                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-3 leading-tight">
                                    {article.title}
                                  </h3>
                                  <p className="text-base text-ink-soft leading-relaxed mb-5">
                                    {article.excerpt}
                                  </p>
                                  <Link
                                    to={`/articles/${article.slug}`}
                                    className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-dark"
                                  >
                                    <span>{t("lectures.tabs.readMore")}</span>
                                    <Icon icon="lucide:arrow-right" />
                                  </Link>
                                </div>
                              </article>
                            </Reveal>
                          );
                        })}
                      </div>

                      {/* Load more, in place.
                          The reader stays on /lectures and more cards simply
                          appear — no navigation, no reload, no scroll jump.
                          The button leaves the DOM once everything is shown,
                          so the count has to live in the button's own label:
                          an aria-live region on it would never fire after
                          the final click, because the button is gone by then. */}
                      {columnRemaining > 0 && (
                        <div className="text-center mt-10">
                          <button
                            type="button"
                            onClick={() =>
                              setColumnVisible((v) => v + COLUMN_PAGE_SIZE)
                            }
                            aria-label={t("lectures.tabs.loadMoreRemaining", {
                              count: columnRemaining,
                            })}
                            className="inline-flex items-center gap-2 border-2 border-primary text-primary px-7 py-3 rounded-yada-full font-semibold transition-colors hover:bg-primary hover:text-surface focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
                          >
                            <span aria-hidden>
                              {t("lectures.tabs.loadMoreRemaining", {
                                count: columnRemaining,
                              })}
                            </span>
                            <Icon icon="lucide:chevron-down" aria-hidden />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  <div className="text-center mt-8">
                    <Link
                      to="/articles"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-dark"
                    >
                      <span>{t("lectures.tabs.allArticles")}</span>
                      <Icon icon="lucide:arrow-right" />
                    </Link>
                  </div>
                </div>
              )
            }
          </Tabs>
        </Container>
      </section>

      {/* Lecture list */}
      <section
        className="py-section-y px-section-x bg-surface-2"
        aria-labelledby="lectures-archive-title"
      >
        <Container size="xl">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-7">
              <H2
                id="lectures-archive-title"
              >
                {slotT("lectures.archive.title")}
              </H2>
            </div>
          </Reveal>

          <div className="grid gap-gap-cards md:grid-cols-2">
            {displayedLectures.map((lecture, idx) => {
              // Normalise Lecture → NormalizedLecture so the shared
              // `<LectureCard>` can render the homepage and archive variants
              // from one component. See src/components/cards/LectureCard.tsx.
              const lang = i18n.language
              const title = pickLocalized(
                lang,
                lecture.titleHans,
                lecture.titleZh,
                lecture.titleEn,
              )
              const summary = pickLocalized(
                lang,
                lecture.summaryHans,
                lecture.summaryZh,
                lecture.summaryEn,
              )
              const venue = pickLocalized(
                lang,
                lecture.venueHans,
                lecture.venueZh,
                lecture.venueEn,
              )
              // 2026-10-25: was an `isZh` ternary, which served the
              // Traditional alt to a 简体中文 reader. The alt has the same
              // three-locale shape as the fields above, so it uses the same
              // picker.
              const photoAlt = pickLocalized(
                lang,
                lecture.photo1AltHans,
                lecture.photo1AltZh,
                lecture.photo1AltEn,
              )
              const audienceTagKey = `lectures.detail.tag${lecture.audience.charAt(0).toUpperCase() + lecture.audience.slice(1)}`
              const normalized: NormalizedLecture = {
                slug: lecture.slug,
                title,
                summary,
                audience: lecture.audience,
                date: lecture.date,
                venue,
                photoUrl: lecture.photo1Thumb || undefined,
                photoAlt: photoAlt || undefined,
                photoWidth: lecture.photo1Width || undefined,
                photoHeight: lecture.photo1Height || undefined,
                externalUrl: lecture.externalUrl,
                venueUrl: lecture.venueUrl,
              }
              return (
                <Reveal
                  key={lecture.slug}
                  delay={Math.min(idx, REVEAL_STAGGER_CAP) * 0.05}
                  className="h-full"
                >
                  <LectureCard
                    lecture={normalized}
                    size="regular"
                    className="bg-white!"
                    ctaLabel={slotT("lectures.detail.readFullTalk")}
                    externalCtaLabel={slotT("lectures.detail.readOnSite")}
                    audienceBadgeLabel={t(audienceTagKey)}
                    externalVenueLabel={slotT("lectures.detail.venueWebsite")}
                    locale={i18n.language}
                  />
                </Reveal>
              )
            })}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="py-section-y px-section-x bg-gradient-to-br from-primary via-primary-dark to-yada-olive text-surface">
        <Container>
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <Icon
                icon="lucide:megaphone"
                className="text-accent mx-auto mb-6"
                style={{ fontSize: "4rem" }}
              />
              <a
                href={`https://wa.me/85268876400?text=${encodeURIComponent(slotT("lectures.hero.title"))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-accent text-ink px-7 py-3 rounded-yada-full font-semibold hover:bg-accent-soft transition-colors"
              >
                <Icon icon="lucide:message-circle" />
                {slotT("lectures.cta.bookDemo")}
              </a>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
